import { Component, DestroyRef, OnDestroy, OnInit, effect, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import cloneDeep from 'lodash-es/cloneDeep';
import remove from 'lodash-es/remove';
import { Observable, Subscription, forkJoin, switchMap } from 'rxjs';
import { FeatureFlagEnum } from 'shared/interfaces/environment.interface';
import { ApplicationPermissionArray } from 'shared/interfaces/permissions.interface';
import { TabComponentInterface } from 'shared/interfaces/tab-component.interface';
import { UserApiService } from 'shared/services';
import { EventBusService } from 'shared/services/event-bus.service';
import { FeatureFlagService } from 'shared/services/feature-flag.service';
import { FormDifferService } from 'shared/services/form-differ.service';
import { ModalService } from 'shared/services/modal.service';
import { ToastService } from 'shared/services/toast.service';
import { cleanGQLData } from 'shared/util/clean-GQL-data';
import { unsubscribeFromAll } from 'shared/util/unsubscribeFromAll';
import { ResourcePermission, User, UserGroup, UserPermission } from 'users/interfaces/user.interface';
import { VisualPermissionResponse } from 'users/interfaces/visual-permissions.interface';
import { SupplierUsersService } from 'users/services/supplier-users.service';
import { UserGroupsService } from 'users/services/user-groups.service';
import { VisualsPermissionsService } from 'visuals/services/visuals-permissions.service';

@Component({
    selector: 'app-edit-user-permissions',
    templateUrl: './supplier-user-edit-permissions.component.html',
    styleUrls: ['./supplier-user-edit-permissions.component.scss'],
    standalone: false,
})
export class SupplierUserEditPermissionsComponent implements OnInit, TabComponentInterface, OnDestroy {
    public data!: User;
    public userGroup!: Partial<UserGroup> | undefined;
    public hasPreviousUserGroup!: boolean;
    public loadingPermissions: boolean = true;
    public groups?: Partial<UserGroup>[];
    public editUserPermissionForm: UntypedFormGroup = this.formBuilder.group({
        userGroupId: new UntypedFormControl(undefined, Validators.required),
        permissions: new UntypedFormControl(undefined),
    });

    private subscriptions: { [key: string]: Subscription | undefined } = {};
    private user!: User;
    protected loggedInUserGroupId: string = String.EMPTY;
    visualPermissions = signal<VisualPermissionResponse[]>([]);
    supplierUserHasAccessToVisualsArea = signal<boolean>(false);

    constructor(
        private formBuilder: UntypedFormBuilder,
        private usersService: SupplierUsersService,
        private eventBusService: EventBusService,
        private toastService: ToastService,
        private translate: TranslateService,
        private userGroupService: UserGroupsService,
        private userApiService: UserApiService,
        private formDifferService: FormDifferService,
        private destroyRef: DestroyRef,
        private modalService: ModalService,
        private featureFlagService: FeatureFlagService,
        private visualPermissionsService: VisualsPermissionsService,
    ) {
        effect(() => {
            const closeModalContentPermissions = this.usersService.closeSupplierUsersModal();
            const closeModalVisualPermissions = this.visualPermissionsService.closeSupplierUsersModal();
            const visualPermissionsEnabled =
                this.featureFlagService.featureFlags()[FeatureFlagEnum.CONTENT_VISUALS_SPLIT] && this.supplierUserHasAccessToVisualsArea();

            if (closeModalContentPermissions.shouldClose && (!visualPermissionsEnabled || closeModalVisualPermissions === true)) {
                this.usersService.closeSupplierUsersModal.set({ shouldClose: false, refreshGrid: false });
                this.visualPermissionsService.closeSupplierUsersModal.set(false);
                this.closeModal(closeModalContentPermissions.refreshGrid);
            }
        });
    }

    public ngOnInit(): void {
        this.user = cloneDeep(this.data);
        this.subscriptions.modalMainAction = this.eventBusService.getObservableForEventStream('modalMainButtonAction').subscribe((data) => {
            if (data === 'permissions') {
                this.updateUserPermissions();
            }
        });
        this.patchPermissions();
    }

    public ngOnDestroy() {
        unsubscribeFromAll(this.subscriptions);
    }

    public canDeactivateTab(): boolean {
        const canDeactivate = !this.formDifferService.hasDataChanged();

        if (!canDeactivate) {
            this.toastService.showToast('generalKeywords.unsavedTabChanges', 'error', {}, 8000);
        }

        return canDeactivate;
    }

    private patchPermissions() {
        this.loadingPermissions = true;
        this.subscriptions.getAllPermissions = forkJoin([
            this.usersService.getSupplierUserById(this.data.id!),
            this.userGroupService.getAllUserGroupsBySupplierIds(this.data.supplierId, false),
            this.userApiService.getLoginInformation(),
        ]).subscribe(([user, groups, loginInfo]) => {
            this.groups = groups || [];
            this.loggedInUserGroupId = (loginInfo as unknown as User)?.userAccesses?.map((access) => access.userGroupId)[0] ?? String.EMPTY;
            this.hasPreviousUserGroup = user?.hasPreviousUserGroup || false;
            this.userGroup = this.hasPreviousUserGroup ? undefined : this.groups.find((x) => x.id == user?.userAccesses[0]?.userGroupId);

            this.supplierUserHasAccessToVisualsArea.set(user.hasVisualsUIEnabled ?? false);

            if (user.visualPermissions) {
                this.visualPermissions.set(user.visualPermissions);
            }

            if (this.hasPreviousUserGroup) {
                this.editUserPermissionForm.removeControl('userGroupId');
            } else {
                const userGroupCtr = this.editUserPermissionForm.get('userGroupId');
                userGroupCtr?.setValue(this.userGroup?.id);
                this.subscriptions.userGroupCtr?.unsubscribe();
                this.subscriptions.userGroupCtr = userGroupCtr?.valueChanges.subscribe((v: string) => {
                    //should get all permissions group
                    this.refreshPermissionsToUserGroup(v);
                });
            }

            const permission: ApplicationPermissionArray = [
                user?.resourcePermissions as Partial<ResourcePermission>[],
                user?.internalPermissions || [],
                user?.visualPermissions || [],
            ];
            this.editUserPermissionForm.get('permissions')?.patchValue(cleanGQLData(permission));
            this.formDifferService.registerForm(this.editUserPermissionForm);
            this.formDifferService.createDataSnapshot();
        });
        this.subscriptions.getAllPermissions.add(() => (this.loadingPermissions = false));
        return this.subscriptions.getAllPermissions;
    }

    private refreshPermissionsToUserGroup(userGroupId: string) {
        this.loadingPermissions = true;
        this.subscriptions.groupPermissions = this.userGroupService.getPermissionsByUserGroupId(userGroupId).subscribe((permissions) => {
            this.editUserPermissionForm.get('permissions')?.patchValue(cleanGQLData(permissions));
            this.formDifferService.createDataSnapshot();
        });
        this.subscriptions.groupPermissions.add(() => (this.loadingPermissions = false));
    }

    public resetToGroupPermission() {
        if (!this.hasPreviousUserGroup) {
            return;
        }

        this.modalService
            .openSimpleModal(
                this.translate.instant('users.supplierUsers.edit.permissions.confirmResetToUserGroup'),
                this.translate.instant('users.supplierUsers.edit.permissions.resetToUserGroupTitle'),
                this.translate.instant('generalKeywords.reset'),
                this.translate.instant('generalKeywords.cancel'),
                true,
                'top',
            )
            .onClose.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((res) => {
                if (!res) {
                    return;
                }

                this.editUserPermissionForm.addControl('userGroupId', new UntypedFormControl(this.userGroup?.id, Validators.required));

                this.loadingPermissions = true;
                this.usersService
                    .restorePreviousUserAccess(this.data.id!)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(() => {
                        this.subscriptions?.getAllPermissions?.unsubscribe();
                        this.patchPermissions().add(() => {
                            this.toastService.showToast('users.supplierUsers.edit.permissions.resetSuccess', 'success', {
                                userFirstName: this.user.firstName,
                                userLastName: this.user.lastName,
                                groupName: this.userGroup?.name,
                            });
                            this.usersService.closeSupplierUsersModal.set({
                                shouldClose: true,
                                refreshGrid: true,
                            });
                        });
                    });
            });
    }

    public updateUserPermissions() {
        this.editUserPermissionForm.markAllAsTouched();
        this.editUserPermissionForm.get('permissions')?.updateValueAndValidity();

        if (this.editUserPermissionForm.invalid) {
            return;
        }

        if (!this.hasPreviousUserGroup && this.userGroup && this.userGroup.id !== this.editUserPermissionForm.value.userGroupId) {
            this.usersService
                .moveUserToGroup(this.data.id, this.editUserPermissionForm.value.userGroupId)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => this.updatePermissions(true));
        } else {
            this.updatePermissions();
        }
    }

    private updatePermissions(wasMovedToAnotherGroup: boolean = false) {
        const permissionObject: any = this.userGroupService.separateResourcePermissionRequests(
            this.editUserPermissionForm.value.permissions,
            (this.formDifferService.getSnapshotData() as any).permissions,
            true,
        );

        const calls: { permission: ResourcePermission[] | UserPermission[]; serviceFunction: any }[] = [];
        ['resourcePermissions', 'userPermissions'].forEach((permissionType) => {
            ['create', 'update', 'delete'].forEach((action) => {
                if (permissionObject[permissionType]?.[action].length) {
                    const functionName = action + permissionType.capitalizeFirstLetter();
                    const serviceFunction = (this.userGroupService as any)[functionName].bind(this.userGroupService);
                    const individualPermission = permissionObject[permissionType][action];
                    if (individualPermission.length) {
                        individualPermission.forEach((x: any) => (x.userId = this.data.id));
                    }
                    calls.push({ permission: individualPermission, serviceFunction: serviceFunction });
                }
            });
        });

        if (!calls.length) {
            if (wasMovedToAnotherGroup) {
                this.toastService.showToast('users.supplierUsers.edit.basic.updateSuccess', 'success', {
                    firstName: this.user.firstName,
                    lastName: this.user.lastName,
                });
                this.usersService.closeSupplierUsersModal.set({
                    shouldClose: true,
                    refreshGrid: true,
                });
            } else {
                this.usersService.closeSupplierUsersModal.set({
                    shouldClose: true,
                    refreshGrid: false,
                });
            }
            this.eventBusService.dispatch('updateSupplierUserVisualPermissions', undefined, true);
            return;
        }

        if (this.hasPreviousUserGroup || calls.length === 1) {
            this.subscriptions.forkJoin = forkJoin(calls.map((x) => x.serviceFunction(x.permission) as Observable<any>)).subscribe(
                (response) => {
                    this.toastService.showToast('users.supplierUsers.edit.basic.updateSuccess', 'success', {
                        firstName: this.user.firstName,
                        lastName: this.user.lastName,
                    });
                    this.usersService.closeSupplierUsersModal.set({
                        shouldClose: true,
                        refreshGrid: true,
                    });
                    this.eventBusService.dispatch('updateSupplierUserVisualPermissions', undefined, true);
                },
            );
        } else {
            const firstUpdate = remove(calls, calls[0])[0];
            const firstCall: Observable<any> = firstUpdate.serviceFunction(firstUpdate.permission);
            this.subscriptions.firstCall = firstCall
                .pipe(
                    switchMap((val) => {
                        return this.usersService.getSupplierUserById(this.data.id!);
                    }),
                    switchMap((user) => {
                        const hasPreviousUserGroup = user.hasPreviousUserGroup;
                        if (!hasPreviousUserGroup) {
                            throw 'Not updated';
                        }
                        const latestPermissions: ApplicationPermissionArray = [
                            user.resourcePermissions as Partial<ResourcePermission>[],
                            user.internalPermissions,
                            user.visualPermissions || [],
                        ];
                        const obsers: Observable<any>[] = [];
                        calls.forEach((c) => {
                            const permRes = c.permission as ResourcePermission[];
                            const permInternal = c.permission as UserPermission[];
                            if (permRes.every((x) => x.resourceId)) {
                                permRes.forEach((r) => {
                                    const latest = latestPermissions[0].find((x) => x.resourceId === r.resourceId);
                                    r.id = latest?.id ?? '';
                                });
                            } else if (permInternal.every((x) => x.permissionAccessZone)) {
                                permInternal.forEach((i) => {
                                    const latest = latestPermissions[1].find((x) => x.permissionAccessZone === i.permissionAccessZone);
                                    i.id = latest?.id ?? '';
                                });
                            } else {
                                return;
                            }
                            obsers.push(c.serviceFunction(c.permission));
                        });
                        return forkJoin(obsers);
                    }),
                )
                .subscribe((response) => {
                    this.toastService.showToast('users.supplierUsers.edit.basic.updateSuccess', 'success', {
                        firstName: this.user.firstName,
                        lastName: this.user.lastName,
                    });
                    this.usersService.closeSupplierUsersModal.set({
                        shouldClose: true,
                        refreshGrid: true,
                    });
                    this.eventBusService.dispatch('updateSupplierUserVisualPermissions', undefined, true);
                });
        }
    }

    public closeModal(refreshGrid: boolean = false) {
        this.eventBusService.dispatch('closeUserModal', refreshGrid);
    }
}
